import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useJsApiLoader } from "@react-google-maps/api";

const BlinkAnalyzer = () => {
  const [clusters, setClusters] = useState([]);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const searchInputRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places", "drawing"]
  });

  useEffect(() => {
    fetch("/api/blink-analyzer")
      .then((res) => res.json())
      .then((data) => setClusters(data))
      .catch((err) => console.error("Failed to fetch blink analyzer data", err));
  }, []);

  const getMapCenter = () => {
    if (clusters.length === 0) return { lat: 39.8, lng: -88.4 };
    const avgLat = clusters.reduce((sum, z) => sum + z.lat, 0) / clusters.length;
    const avgLng = clusters.reduce((sum, z) => sum + z.lon, 0) / clusters.length;
    return { lat: avgLat, lng: avgLng };
  };

  useEffect(() => {
    if (!isLoaded || !mapRef.current || clusters.length === 0) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: getMapCenter(),
      zoom: 11,
      mapTypeControl: false,
    });

    const markers = clusters.map((zone) => {
      const marker = new window.google.maps.Marker({
        position: { lat: zone.lat, lng: zone.lon },
        title: `Blink: ${zone.predictedCause}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: "orange",
          fillOpacity: 0.8,
          strokeWeight: 1,
        },
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="font-size:14px">
            <strong>Cause:</strong> ${zone.predictedCause}<br/>
            <strong>Blink Count:</strong> ${zone.blinkCount}<br/>
            <strong>Devices:</strong> ${zone.sampleDevices.join(", ")}<br/>
            <strong>Time Window:</strong> ${zone.timeWindow}
          </div>
        `,
      });

      marker.addListener("click", () => infoWindow.open(map, marker));
      return marker;
    });

    if (window.MarkerClusterer && markers.length > 0) {
      new window.MarkerClusterer({ markers, map });
    } else {
      markers.forEach((m) => m.setMap(map));
    }

    markersRef.current = markers;

    if (searchInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current);
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          map.panTo(place.geometry.location);
          map.setZoom(14);
        }
      });
    }

    const drawingManager = new window.google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: true,
      drawingControlOptions: {
        position: window.google.maps.ControlPosition.TOP_CENTER,
        drawingModes: ["circle", "rectangle", "polygon"],
      },
      circleOptions: {
        fillColor: "#4285F4",
        fillOpacity: 0.2,
        strokeWeight: 2,
        clickable: false,
        editable: true,
        zIndex: 1,
      },
    });
    drawingManager.setMap(map);
    drawingManagerRef.current = drawingManager;

    const streetViewControlDiv = document.createElement("div");
    const controlUI = document.createElement("button");
    controlUI.innerText = "Street View";
    controlUI.style.padding = "6px 10px";
    controlUI.style.margin = "10px";
    controlUI.style.fontSize = "14px";
    controlUI.style.background = "#fff";
    controlUI.style.border = "1px solid #ccc";
    controlUI.style.cursor = "pointer";
    streetViewControlDiv.appendChild(controlUI);
    map.controls[window.google.maps.ControlPosition.RIGHT_TOP].push(streetViewControlDiv);

    controlUI.addEventListener("click", () => {
      const toggle = map.getStreetView();
      toggle.setPosition(map.getCenter());
      toggle.setVisible(!toggle.getVisible());
    });
  }, [isLoaded, clusters]);

  if (!isLoaded) {
    return <div style={{ padding: "2rem" }}>Loading Google Maps...</div>;
  }

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      <div style={{ padding: "1rem" }}>
        <h2>Blink Analyzer</h2>
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            margin: "1rem",
            padding: "8px 15px",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
          }}
        >
          Back to Dashboard
        </button>

        <input
          type="text"
          ref={searchInputRef}
          placeholder="Search address..."
          style={{
            width: "300px",
            padding: "8px",
            fontSize: "14px",
            marginTop: "1rem",
          }}
        />
      </div>

      {clusters.length > 0 ? (
        <div ref={mapRef} style={{ height: "85%", width: "100%" }} />
      ) : (
        <div
          style={{
            height: "85%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <p>No data available for blink analysis. Please check back later.</p>
        </div>
      )}
    </div>
  );
};

export default BlinkAnalyzer;
